/** Optional "CLOSDD Sales Profile" a salesperson can build so an employer can decide fast. */

export type ShowcaseRole = {
  id: string;
  company: string;
  title: string;
  period: string;
  sold: string;
  dealSize: string;
  quota: string;
  highlight: string;
};

export type ShowcaseAnswer = {
  id: string;
  question: string;
  answer: string;
};

/** One recorded audio answer from the CLOSDD sales assessment. */
export type VoiceTake = {
  url: string;
  seconds: number;
  recordedAt: string;
  /**
   * Admin-set wording of this question for this one candidate, replacing the
   * standard prompt. May exist with an empty url (question changed, no
   * answer recorded yet).
   */
  question?: string;
};

export type Visibility = "public" | "employers" | "private";

export type ShowcaseProfile = {
  enabled: boolean;
  slug: string;
  visibility: Visibility;
  headline: string;
  targetRole: string;
  location: string;
  availability: string;
  bio: string;
  videoUrl: string;
  audioUrl: string;
  audioSeconds: number;
  yearsSelling: string;
  quotaHit: string;
  biggestDeal: string;
  avgCycle: string;
  revenue: string;
  dealsClosed: string;
  avgDealSize: string;
  soldProducts: string;
  clients: string;
  markets: string;
  tools: string;
  skills: string;
  skillTags: string[];
  environment: string[];
  industries: string;
  linkedin: string;
  roles: ShowcaseRole[];
  answers: ShowcaseAnswer[];
  assessment: Record<string, VoiceTake>;
  targetRoles: string[];
  employmentTypes: string[];
  locationPref: string;
  availabilityWhen: string;
  expectations: string;
  /** Salary expectation band, e.g. "Up to £45k". */
  salaryExpectation: string;
  /**
   * Public-profile sections an admin has unlocked for logged-out visitors
   * (see UnlockableSection). Only admins can set it.
   */
  unlockedSections?: string[];
  /** Admin-set badge ids (see candidateTags in tags.ts). Only admins can set it. */
  candidateTags?: string[];
  savedAt: string;
};

/**
 * One row of the real candidate directory (GET /admin/candidates,
 * GET /employer/candidates) — replaces the old hardcoded salesDatabase.
 */
export type CandidateDirectoryEntry = {
  id: string;
  name: string;
  email: string;
  phone: string;
  /** Country/region picked on their profile (or by an admin on creation) — distinct from showcase.location, their free-text preferred work location. */
  location: string | null;
  /** "Closers" or "Setters" — only ever set by an admin on the New Candidate form; self-signed-up candidates won't have this. */
  role: string | null;
  cvUrl: string | null;
  cvName?: string | null;
  savedAt: string | null;
  lastLoginAt: string | null;
  /** True for candidates created directly via the admin "New candidate" form as sample/demo data. */
  demo: boolean;
  /** Created by an admin through the New candidate form, not a signup. */
  createdByAdmin?: boolean;
  /** Admin-only — the API never sends these to employers. */
  experienceLevel?: string | null;
  salesTypes?: string[];
  showcase: ShowcaseProfile;
};

const KEY = "closdd.candidate.showcase";

export const defaultAnswers: ShowcaseAnswer[] = [
  { id: "a1", question: "How do you open a cold conversation?", answer: "" },
  { id: "a2", question: "Walk me through a deal you nearly lost and won back.", answer: "" },
  { id: "a3", question: "How do you handle a hard price objection?", answer: "" },
];

/** The five voice questions employers listen to. */
export const assessmentQuestions: {
  id: string;
  label: string;
  title: string;
  prompt: string;
  maxSeconds: number;
  guide: string;
}[] = [
  {
    id: "q1",
    label: "Sell me this",
    title: "Sell yourself",
    prompt:
      "Imagine I am a recruiter and you have just 60 seconds to convince me why I should interview you for a sales role. Sell yourself.",
    maxSeconds: 60,
    guide: "Maximum 60 seconds",
  },
  {
    id: "q2",
    label: "Discovery & questioning",
    title: "Discovery",
    prompt:
      "You are speaking with a potential customer who says they are happy with their current provider. What questions would you ask to understand whether there is still an opportunity to help them?",
    maxSeconds: 90,
    guide: "Around 60–90 seconds",
  },
  {
    id: "q3",
    label: "Objection handling",
    title: "Objection handling",
    prompt:
      "A prospect says: \u201CYour product sounds good, but it is too expensive.\u201D Respond to the objection as if you were speaking directly to the prospect.",
    maxSeconds: 90,
    guide: "Around 60–90 seconds",
  },
  {
    id: "q4",
    label: "Closing",
    title: "Closing",
    prompt:
      "You have had a strong sales conversation. The prospect likes the product but says they want to think about it. How would you respond and move the conversation forward?",
    maxSeconds: 90,
    guide: "Around 60–90 seconds",
  },
  {
    id: "q5",
    label: "Sales thinking",
    title: "Sales thinking",
    prompt:
      "Tell us about a difficult sales situation or objection you have faced and explain how you handled it.",
    maxSeconds: 90,
    guide: "Around 60–90 seconds",
  },
];

/**
 * Key in ShowcaseProfile.assessment holding an admin's custom wording of the
 * voice introduction prompt for one candidate — question only, no recording
 * (the recording itself is audioUrl). Not an assessment question, so it
 * never counts towards assessmentDone().
 */
export const VOICE_INTRO_KEY = "intro";

/** The voice introduction prompt as this candidate was asked it. */
export function voiceIntroPromptFor(p: ShowcaseProfile) {
  return p.assessment[VOICE_INTRO_KEY]?.question?.trim() || voiceIntroPrompt;
}

/** The 60-second voice introduction prompt. */
export const voiceIntroPrompt =
  "Imagine you are introducing yourself to a hiring manager. In 60 seconds, tell them who you are, what sales experience you have, what you are good at and why they should speak to you.";

export const skillOptions = [
  "Closing",
  "Discovery",
  "Objection Handling",
  "Negotiation",
  "Prospecting",
  "Cold Calling",
  "Appointment Setting",
  "Account Management",
  "Consultative Selling",
  "Pipeline Management",
  "Lead Qualification",
  "Relationship Building",
  "Upselling",
  "Cross-selling",
  "CRM Management",
];

export const environmentOptions = [
  "B2B",
  "B2C",
  "Inbound",
  "Outbound",
  "Full sales cycle",
  "Closing",
  "Appointment setting",
  "Business development",
];

export const targetRoleOptions = [
  "Sales Executive",
  "Account Executive",
  "Business Development Representative",
  "Sales Development Representative",
  "Account Manager",
  "Business Development Manager",
  "Appointment Setter",
  "Telesales",
  "Fundraiser",
  "Recruitment Consultant",
  "High-Ticket Closer",
  "Sales Manager",
  "Other",
];

export const employmentTypeOptions = ["Full-time", "Part-time", "Contract", "Commission only", "Flexible"];

export const locationPrefOptions = ["Remote", "Hybrid", "On-site"];

export const availabilityWhenOptions = ["Immediately", "2 weeks", "1 month", "Other"];

/** Scoring categories admins will grade against later. Not shown to employers yet. */
export const assessmentScoringCategories = [
  "Communication",
  "Discovery",
  "Objection Handling",
  "Closing",
  "Commercial Thinking",
  "Confidence",
];

export const emptyShowcase: ShowcaseProfile = {
  enabled: false,
  slug: "",
  visibility: "employers",
  headline: "",
  targetRole: "",
  location: "",
  availability: "",
  bio: "",
  videoUrl: "",
  audioUrl: "",
  audioSeconds: 0,
  yearsSelling: "",
  quotaHit: "",
  biggestDeal: "",
  avgCycle: "",
  revenue: "",
  dealsClosed: "",
  avgDealSize: "",
  soldProducts: "",
  clients: "",
  markets: "",
  tools: "",
  skills: "",
  skillTags: [],
  environment: [],
  industries: "",
  linkedin: "",
  roles: [],
  answers: defaultAnswers,
  assessment: {},
  targetRoles: [],
  employmentTypes: [],
  locationPref: "",
  availabilityWhen: "",
  expectations: "",
  salaryExpectation: "",
  savedAt: "",
};

export function emptyRole(): ShowcaseRole {
  return {
    id: Math.random().toString(36).slice(2, 9),
    company: "",
    title: "",
    period: "",
    sold: "",
    dealSize: "",
    quota: "",
    highlight: "",
  };
}

/** Sections of the public profile that are locked for logged-out visitors unless an admin unlocks them. */
export const unlockableSections = [
  { key: "name", label: "Full name", hint: "Otherwise first name only" },
  { key: "contact", label: "Contact details", hint: "Email and phone" },
  { key: "about", label: "About", hint: "Their background" },
  { key: "cv", label: "CV", hint: "Download link" },
] as const;

export type UnlockableSection = (typeof unlockableSections)[number]["key"];

/** Stable, readable slug for the shareable profile URL. */
export function slugFor(name: string, fallbackSeed?: string) {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
  const seed = (fallbackSeed ?? Math.random().toString(36).slice(2, 6)).replace(/[^a-z0-9]/gi, "").slice(0, 4);
  return `${base || "salesperson"}-${(seed || "clsd").toLowerCase()}`;
}

const DEMO_AUDIO = "/demo/voice-sample.wav";

/** Name shown on the demo profile until a candidate saves their own details. */
export const demoCandidateName = "Jordan Whitfield";

function take(seconds: number): VoiceTake {
  return { url: DEMO_AUDIO, seconds, recordedAt: new Date().toISOString() };
}

/**
 * A fully completed example profile so the employer view and candidate view can
 * be seen end to end before a real candidate fills theirs in.
 */
export const demoShowcase: ShowcaseProfile = {
  ...emptyShowcase,
  enabled: true,
  slug: "jordan-whitfield-clsd",
  visibility: "public",
  headline: "B2B SaaS closer — £40k–£150k deals, mid-market UK",
  targetRole: "Senior Account Executive",
  location: "Manchester, UK",
  availability: "Actively looking",
  bio:
    "Six years selling into mid-market and enterprise UK businesses, the last four in B2B SaaS. I build my own pipeline through outbound, run full cycle from first call to signature, and I'm strongest in multi-stakeholder deals where procurement and finance get involved. Last year I closed £1.4m of new business against a £1.1m number, with 42% of it self-sourced.",
  videoUrl: "",
  audioUrl: DEMO_AUDIO,
  audioSeconds: 58,
  yearsSelling: "6",
  quotaHit: "127%",
  biggestDeal: "£148,000",
  avgCycle: "46 days",
  revenue: "£1.4m",
  dealsClosed: "38 last year",
  avgDealSize: "£37,000",
  soldProducts: "B2B SaaS platforms, workforce software, payments, data subscriptions",
  clients: "Bupa, DPD, Zilch, The Hut Group, Vodafone Business, mid-market SME",
  markets: "UK & Ireland, some EMEA",
  tools: "Salesforce, HubSpot, Outreach, Gong, Apollo, LinkedIn Sales Navigator",
  skills: "Outbound, discovery, multi-threading, negotiation, forecasting",
  skillTags: [
    "Closing",
    "Discovery",
    "Objection Handling",
    "Negotiation",
    "Prospecting",
    "Pipeline Management",
    "Consultative Selling",
  ],
  environment: ["B2B", "Outbound", "Full sales cycle", "Closing"],
  industries: "SaaS, Technology, Fintech",
  linkedin: "https://linkedin.com/in/jordanwhitfield",
  roles: [
    {
      id: "r1",
      company: "Northgate Software",
      title: "Senior Account Executive",
      period: "2023 — present",
      sold: "Workforce management SaaS to mid-market operations teams",
      dealSize: "£25k — £148k ACV",
      quota: "127% of £1.1m",
      highlight:
        "Top of a team of nine for two consecutive years. Built an outbound motion into logistics that now produces a third of the team's pipeline.",
    },
    {
      id: "r2",
      company: "Vantive Data",
      title: "Account Executive",
      period: "2021 — 2023",
      sold: "Data subscriptions to marketing and insight teams",
      dealSize: "£8k — £40k ACV",
      quota: "112% average",
      highlight: "Fastest ramp in company history — hit full quota in month four and closed the largest deal of 2022.",
    },
    {
      id: "r3",
      company: "Halo Telecom",
      title: "Business Development Executive",
      period: "2019 — 2021",
      sold: "Business connectivity and phone systems to SMEs",
      dealSize: "£1.2k — £9k",
      quota: "104%",
      highlight: "100+ cold calls a day. Learned discovery and objection handling the hard way and never lost the habit.",
    },
  ],
  answers: defaultAnswers,
  assessment: {
    q1: take(57),
    q2: take(82),
    q3: take(74),
    q4: take(68),
    q5: take(88),
  },
  targetRoles: ["Account Executive", "Business Development Manager", "Sales Manager"],
  employmentTypes: ["Full-time"],
  locationPref: "Hybrid",
  availabilityWhen: "2 weeks",
  expectations: "£45k–£55k base, £90k+ OTE, uncapped commission",
  salaryExpectation: "Up to £50k",
  savedAt: new Date().toISOString(),
};

export function loadShowcase(): ShowcaseProfile {
  if (typeof window === "undefined") return demoShowcase;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return demoShowcase;

    const parsed = JSON.parse(raw) as Partial<ShowcaseProfile>;
    return {
      ...emptyShowcase,
      ...parsed,
      roles: parsed.roles ?? [],
      answers: parsed.answers?.length ? parsed.answers : defaultAnswers,
      assessment: parsed.assessment ?? {},
      skillTags: parsed.skillTags ?? [],
      environment: parsed.environment ?? [],
      targetRoles: parsed.targetRoles ?? [],
      employmentTypes: parsed.employmentTypes ?? [],
    };
  } catch {
    return emptyShowcase;
  }
}

/**
 * The candidate's OWN saved profile, with no demo fallback. Used anywhere the
 * app must judge real completion (e.g. whether they can apply for a role).
 */
export function loadOwnShowcase(): ShowcaseProfile {
  if (typeof window === "undefined") return emptyShowcase;
  if (!window.localStorage.getItem(KEY)) return emptyShowcase;
  return loadShowcase();
}

export function saveShowcase(p: Partial<ShowcaseProfile>): ShowcaseProfile {
  const next: ShowcaseProfile = {
    ...loadShowcase(),
    ...p,
    savedAt: new Date().toISOString(),
  };
  if (typeof window !== "undefined") window.localStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

export function splitList(value: string): string[] {
  if (!value) return [];
  return value
    .split(/[,\n]/)
    .map((v) => v.trim())
    .filter(Boolean);
}

/** The question as this candidate was asked it — their custom wording if an admin set one, else the standard prompt. */
export function assessmentPrompt(take: VoiceTake | undefined, q: { prompt: string }) {
  return take?.question?.trim() || q.prompt;
}

export function assessmentDone(p: ShowcaseProfile) {
  return assessmentQuestions.filter((q) => p.assessment[q.id]?.url).length;
}

export function assessmentComplete(p: ShowcaseProfile) {
  return assessmentDone(p) === assessmentQuestions.length;
}

/** Checklist shown on the dashboard: what's done, what's optional. */
export function profileChecklist(p: ShowcaseProfile, hasBasics: boolean, hasCv: boolean) {
  return [
    { label: "Basic information", ok: hasBasics && Boolean(p.headline), optional: false },
    { label: "CV uploaded", ok: hasCv, optional: false },
    { label: "Voice introduction", ok: Boolean(p.audioUrl), optional: false },
    { label: "Sales assessment", ok: assessmentComplete(p), optional: false },
    { label: "Video introduction", ok: Boolean(p.videoUrl), optional: true },
  ];
}

/**
 * Percentage for the "Required" profile card — only counts the checklist's
 * non-optional items, so finishing every required step (CV, voice intro,
 * assessment, basics) reaches 100% on its own. This used to include the
 * optional Video introduction item in the denominator too, which made
 * 100% unreachable without it despite the card being labelled "Required"
 * and every actually-required step being done.
 */
export function profileScore(p: ShowcaseProfile, hasBasics = true, hasCv = false) {
  const items = profileChecklist(p, hasBasics, hasCv).filter((i) => !i.optional);
  return Math.round((items.filter((i) => i.ok).length / items.length) * 100);
}

export function showcaseStrength(p: ShowcaseProfile) {
  const checks = [
    Boolean(p.headline),
    Boolean(p.bio),
    Boolean(p.videoUrl),
    Boolean(p.audioUrl),
    Boolean(p.soldProducts),
    Boolean(p.clients),
    p.skillTags.length > 0 || Boolean(p.skills),
    p.roles.length > 0,
    assessmentDone(p) > 0,
    Boolean(p.quotaHit || p.biggestDeal || p.revenue),
    p.targetRoles.length > 0,
    Boolean(p.availabilityWhen),
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

export function profileUrl(slug: string) {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://closdd.com";
  return `${origin}/profile/${slug}`;
}
